import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { DeviceDetails } from '../../../API/types/Devices';
import DeviceThumbnail from '../../../assets/images/device-thumbnail.png';
import Modal from '../../../components/common/Modal/Modal';
import Tags from '../../../components/common/Tags';
import { Button } from '../../../components/ui/Button/Button';
import { ListItemWrapper } from '../../../components/ui/ListItemWrapper/ListItemWrapper';
import EntityLink from '../../../components/ui/EntityLink/EntityLink';
import { ThemeType } from '../../../theme/themeType';
import { renderStatus } from '../helpers';
import useVehicleThumbnail from '../../../theme/useVehicleThumbnail';
import { AssignVehicleToDevice } from '../Modals';
import useGetRole from '../../../API/hooks/useGetRole';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import { getDeviceLastActivityLabel } from '../../../utils/dateUtils';
import useGetDeviceConfiguration from '../../../API/hooks/devices/useGetDeviceConfiguration';
import { getDeviceConfigStatusDisplay } from '../../../utils/deviceConfigStatusDisplay';
import { ListNavigationState } from '../../../utils/listNavigation';

type Props = {
  item?: DeviceDetails;
  lightVersion?: boolean;
  listNavigation?: ListNavigationState;
};

function DevicesItem({ item, lightVersion, listNavigation }: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const vehicleThumbnail = useVehicleThumbnail();
  const navigate = useNavigate();
  const {
    isFleetViewer, isAdmin, isSupport, isFleetManager,
  } = useGetRole();
  const [modalState, toggleModalState] = useState(false);

  const {
    _id: id,
    deviceId,
    activeAt,
    hwModel,
    fwVersion,
    isOnline,
    vehicle,
    fleetId,
  } = item!;

  const { label, color } = renderStatus(isOnline);
  const vehicleDetailsId = vehicle?._id || vehicle?.vehicleId || item?.vehicleId;
  const {
    data: deviceConfig,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useGetDeviceConfiguration(deviceId, fleetId ?? '');
  const handleAssignDevice = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalState(true);
  };

  const {
    icon: configIcon,
    color: configColor,
    text: configText,
    textColor: configTextColor,
  } = getDeviceConfigStatusDisplay(deviceConfig?.status, isConfigLoading, isConfigError);

  return (
    <>
      <ListItemWrapper
        onClick={() => navigate(`/devices/${id}`, { state: { listNavigation } })}
        data-list-item-id={id}
      >
        <Tags.Status
          label={label}
          backgroundColor={color}
        />
        <Tags.ItemOverview
          avatar={DeviceThumbnail}
          avatarAlt="device"
          title={hwModel}
          subtitle={deviceId}
          isRoundImage
        />
        <div className="tags-container">
          {fwVersion && (isAdmin || isSupport || isFleetManager) && (
            <Tags.Label label={`FW: ${fwVersion}`} />
          )}
          <Tags.LabeledIcon
            label={configText}
            icon={configIcon}
            backgroundColor={configColor}
            textColor={configTextColor}
            flexible
          />
          <Tags.LabeledIcon
            label={getDeviceLastActivityLabel(item?.activeAt, !!item?.isOnline)}
            icon={IconKeys.TIME}
            flexible
          />
          {!!vehicle && (
            <EntityLink
              to={vehicleDetailsId ? `/vehicles/${vehicleDetailsId}` : undefined}
              label={`Open vehicle ${vehicle.licensePlateNumber}`}
            >
              <Tags.Vehicle
                imageSrc={vehicleThumbnail}
                licensePlateNumber={vehicle.licensePlateNumber}
                model={`${vehicle.manufacturer ? `${vehicle.manufacturer} ` : ''}${vehicle.model || ''}`}
              />
            </EntityLink>
          )}
          {!vehicle && !lightVersion && !isFleetViewer && (
            <Button
              height="52px"
              width="200px"
              padding="24px 17px"
              background={theme.colors.outline}
              hoverBackground={theme.colors.inactive}
              onClick={(e) => handleAssignDevice(e)}
            >
              Assign vehicle
            </Button>
          )}
          {/* <Tags.CollapsedEvents count="6" /> */}
        </div>
        <Tags.ProceedToDetails />
      </ListItemWrapper>
      {modalState && (
        <Modal
          title="Assign vehicle"
          state={modalState}
          toggleState={toggleModalState}
        >
          <AssignVehicleToDevice
            onCancel={() => toggleModalState(false)}
            fleetId={fleetId as string}
            deviceId={id}
            avatar={DeviceThumbnail}
            model={hwModel as string}
          />
        </Modal>
      )}
    </>
  );
}

export default DevicesItem;
